
import { useState, useRef } from "react";
import "./AdvisoryPartnerForm.css";

const STEPS = [
  { label: "Profile", icon: "👤" },
  { label: "Expertise", icon: "🧠" },
  { label: "Engagement", icon: "🤝" },
];

const FIELDS = {
  expertise: [
    "Generative AI & LLM Consulting",
    "Digital Transformation",
    "Data & Business Intelligence",
    "Platform & Infrastructure Advisory",
  ],
  industries: [
    "Healthcare & Life Sciences",
    "Manufacturing & Supply Chain",
    "E-commerce & Retail",
    "Financial & Banking",
  ],
  aiExperience: [
    "Led Enterprise-Level Transformations",
    "Managed Mid-Scale AI Initiatives",
    "Worked as a Functional Expert",
    "Limited Consulting Exposure",
  ],
  aiLevel: [
    "Advanced Implementation Experience",
    "Strong Working Knowledge",
    "Moderate Exposure",
    "Basic Understanding",
  ],
  engagementType: [
    "Executive Strategy Consulting",
    "AI Transformation Roadmaps",
    "Business Process Optimization",
    "Technology Assessment & Planning",
  ],
  involvement: [
    "Strategic Advisory Only",
    "End-to-End Transformation Guidance",
    "Project-Specific Consulting",
    "Flexible Based on Requirement",
  ],
  commercialModel: [
    "Fixed Project Pricing",
    "Monthly Retainer",
    "Revenue Sharing Model",
    "Open to Discussion",
  ],
};

const EMPTY_FORM = {
  fullName: "", contactNumber: "", email: "", organization: "",
  linkedin: "", bio: null, yearsExp: "", expertise: [],
  industries: [], aiExperience: "", aiLevel: "",
  engagementType: [], involvement: "", commercialModel: "",
};

// ── Dedicated validators per step ─────────────────────────────────────────────

function validateStep0(form) {
  const e = {};
  if (!form.fullName.trim()) e.fullName = "Required";
  if (!form.contactNumber.trim()) e.contactNumber = "Required";
  if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) e.email = "Valid email required";
  // if (!form.linkedin.trim()) e.linkedin = "Required";
  return e;
}

function validateStep1(form) {
  const e = {};
  if (!form.yearsExp) e.yearsExp = "Required";
  if (!form.expertise.length) e.expertise = "Select at least one";
  if (!form.industries.length) e.industries = "Select at least one";
  return e;
}

function validateStep2(form) {
  const e = {};
  if (!form.aiExperience) e.aiExperience = "Required";
  if (!form.aiLevel) e.aiLevel = "Required";
  if (!form.involvement) e.involvement = "Required";
  if (!form.commercialModel) e.commercialModel = "Required";
  return e;
}

const STEP_VALIDATORS = [validateStep0, validateStep1, validateStep2];

// ──────────────────────────────────────────────────────────────────────────────

export default function AdvisoryPartnerForm() {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileRef = useRef();

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const toggleMulti = (key, val) =>
    set(key, form[key].includes(val)
      ? form[key].filter(x => x !== val)
      : [...form[key], val]);

  const next = () => {
    const e = STEP_VALIDATORS[step](form);
    if (Object.keys(e).length) {
      setErrors(e);
      return;
    }
    setErrors({});
    setStep(s => s + 1);
  };

  const back = () => {
    setErrors({});
    setStep(s => s - 1);
  };

  const submitToBackend = async (formData) => {
    setIsSubmitting(true);
    const submitForm = new FormData();
    submitForm.append("fullName", formData.fullName);
    submitForm.append("contactNumber", formData.contactNumber);
    submitForm.append("email", formData.email);
    submitForm.append("organization", formData.organization);
    submitForm.append("linkedin", formData.linkedin);
    submitForm.append("yearsExp", formData.yearsExp);
    submitForm.append("expertise", JSON.stringify(formData.expertise));
    submitForm.append("industries", JSON.stringify(formData.industries));
    submitForm.append("aiExperience", formData.aiExperience);
    submitForm.append("aiLevel", formData.aiLevel);
    submitForm.append("engagementType", JSON.stringify(formData.engagementType));
    submitForm.append("involvement", formData.involvement);
    submitForm.append("commercialModel", formData.commercialModel);
    if (formData.bio) submitForm.append("bio", formData.bio);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/advisory-partner/submit`,
        { method: "POST", body: submitForm }
      );
      const result = await response.json();
      if (result.success) {
        setSubmitted(true);
      } else {
        const validationErrors = {};
        if (result.errors) {
          result.errors.forEach(err => { validationErrors[err.param] = err.msg; });
        }
        setErrors(validationErrors);
        alert(result.message || "Please fix the errors in the form");
      }
    } catch (error) {
      console.error("Submission error:", error);
      alert("Failed to submit application. Please check if the server is running and try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const submit = (e) => {
    e.preventDefault();
    const e2 = validateStep2(form);          // always call step-2 validator directly
    if (Object.keys(e2).length) {
      setErrors(e2);
      return;
    }
    setErrors({});
    submitToBackend(form);
  };

  if (submitted) return (
    <div className="apf apf--success">
      <div className="apf__success-icon">✦</div>
      <h2>Application Submitted</h2>
      <p>
        Thank you, {form.fullName}. Our team will review your advisory partner
        application and reach out within 5–7 business days.
      </p>
      <button
        className="apf__btn apf__btn--primary"
        onClick={() => { setSubmitted(false); setStep(0); setErrors({}); setForm({ ...EMPTY_FORM }); }}
      >
        Submit Another
      </button>
    </div>
  );

  return (
    <div className="apf">
      <div className="apf__header">
        <span className="apf__eyebrow">Bairees Advisory Network</span>
        <h1 className="apf__title">Advisory Partner <span>Application</span></h1>
        <p className="apf__subtitle">
          Join our network of senior advisors shaping AI-led enterprise transformation.
        </p>
      </div>

      <div className="apf__stepper">
        {STEPS.map((s, i) => (
          <div
            key={i}
            className={`apf__step ${i === step ? "apf__step--active" : ""} ${i < step ? "apf__step--done" : ""}`}
          >
            <div className="apf__step-dot">{i < step ? "✓" : s.icon}</div>
            <span>{s.label}</span>
          </div>
        ))}
        <div className="apf__step-track">
          <div className="apf__step-fill" style={{ width: `${(step / 2) * 100}%` }} />
        </div>
      </div>

      <form className="apf__form" onSubmit={submit} noValidate>

        {/* ── STEP 0 — Profile ───────────────────────────────────────────── */}
        {step === 0 && (
          <div className="apf__panel">
            <h3 className="apf__panel-title">Personal & Professional Profile</h3>
            <div className="apf__grid apf__grid--2">
              <Field label="Full Name *" error={errors.fullName}>
                <input
                  className={`apf__input ${errors.fullName ? "apf__input--err" : ""}`}
                  value={form.fullName}
                  onChange={e => set("fullName", e.target.value)}
                  placeholder="Dr. Arjun Mehta"
                />
              </Field>
              <Field label="Contact Number *" error={errors.contactNumber}>
                <input
                  className={`apf__input ${errors.contactNumber ? "apf__input--err" : ""}`}
                  value={form.contactNumber}
                  onChange={e => set("contactNumber", e.target.value)}
                  placeholder="+91 98765 43210"
                />
              </Field>
            </div>
            <Field label="Professional Email *" error={errors.email}>
              <input
                className={`apf__input ${errors.email ? "apf__input--err" : ""}`}
                type="email"
                value={form.email}
                onChange={e => set("email", e.target.value)}
                placeholder="advisor@company.com"
              />
            </Field>
            <div className="apf__grid apf__grid--2">
              <Field label="Current Organization & Designation">
                <input
                  className="apf__input"
                  value={form.organization}
                  onChange={e => set("organization", e.target.value)}
                  placeholder="VP Strategy, Infosys"
                />
              </Field>
              <Field label="LinkedIn / Profile URL " error={errors.linkedin}>
                <input
                  className={`apf__input ${errors.linkedin ? "apf__input--err" : ""}`}
                  value={form.linkedin}
                  onChange={e => set("linkedin", e.target.value)}
                  placeholder="linkedin.com/in/yourprofile"
                />
              </Field>
            </div>
            <Field label="Upload BIO / Profile (PDF or Word)">
              <div className="apf__file" onClick={() => fileRef.current.click()}>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".pdf,.doc,.docx"
                  hidden
                  onChange={e => set("bio", e.target.files[0])}
                />
                <span className="apf__file-icon">📎</span>
                <span>{form.bio ? form.bio.name : "Click to upload your profile document"}</span>
              </div>
            </Field>
          </div>
        )}

        {/* ── STEP 1 — Expertise ────────────────────────────────────────── */}
        {step === 1 && (
          <div className="apf__panel">
            <h3 className="apf__panel-title">Expertise & Industry Background</h3>
            <Field label="Years of Experience *" error={errors.yearsExp}>
              <div className="apf__radio-group">
                {["0–5 Years", "5–10 Years", "10–20 Years", "20+ Years"].map(o => (
                  <label
                    key={o}
                    className={`apf__radio ${form.yearsExp === o ? "apf__radio--active" : ""}`}
                  >
                    <input
                      type="radio"
                      name="yearsExp"
                      value={o}
                      checked={form.yearsExp === o}
                      onChange={() => set("yearsExp", o)}
                    />
                    {o}
                  </label>
                ))}
              </div>
            </Field>
            <Field label="Core Areas of Expertise * (select all that apply)" error={errors.expertise}>
              <div className="apf__chip-group">
                {FIELDS.expertise.map(o => (
                  <button
                    key={o}
                    type="button"
                    className={`apf__chip ${form.expertise.includes(o) ? "apf__chip--active" : ""}`}
                    onClick={() => toggleMulti("expertise", o)}
                  >
                    {o}
                  </button>
                ))}
              </div>
            </Field>
            <Field label="Industries Served * (select all that apply)" error={errors.industries}>
              <div className="apf__chip-group">
                {FIELDS.industries.map(o => (
                  <button
                    key={o}
                    type="button"
                    className={`apf__chip ${form.industries.includes(o) ? "apf__chip--active" : ""}`}
                    onClick={() => toggleMulti("industries", o)}
                  >
                    {o}
                  </button>
                ))}
              </div>
            </Field>
          </div>
        )}

        {/* ── STEP 2 — Engagement ───────────────────────────────────────── */}
        {step === 2 && (
          <div className="apf__panel">
            <h3 className="apf__panel-title">Engagement Preferences</h3>
            <Field label="Experience in AI / Digital Transformation *" error={errors.aiExperience}>
              <div className="apf__select-group">
                {FIELDS.aiExperience.map(o => (
                  <label
                    key={o}
                    className={`apf__select-card ${form.aiExperience === o ? "apf__select-card--active" : ""}`}
                  >
                    <input
                      type="radio"
                      name="aiExp"
                      value={o}
                      checked={form.aiExperience === o}
                      onChange={() => set("aiExperience", o)}
                    />
                    {o}
                  </label>
                ))}
              </div>
            </Field>
            <Field label="Familiarity with AI / LLM Technologies *" error={errors.aiLevel}>
              <div className="apf__radio-group">
                {FIELDS.aiLevel.map(o => (
                  <label
                    key={o}
                    className={`apf__radio ${form.aiLevel === o ? "apf__radio--active" : ""}`}
                  >
                    <input
                      type="radio"
                      name="aiLevel"
                      value={o}
                      checked={form.aiLevel === o}
                      onChange={() => set("aiLevel", o)}
                    />
                    {o}
                  </label>
                ))}
              </div>
            </Field>
            <Field label="Preferred Advisory Engagement (select all that apply)">
              <div className="apf__chip-group">
                {FIELDS.engagementType.map(o => (
                  <button
                    key={o}
                    type="button"
                    className={`apf__chip ${form.engagementType.includes(o) ? "apf__chip--active" : ""}`}
                    onClick={() => toggleMulti("engagementType", o)}
                  >
                    {o}
                  </button>
                ))}
              </div>
            </Field>
            <div className="apf__grid apf__grid--2">
              <Field label="Level of Involvement *" error={errors.involvement}>
                <select
                  className={`apf__input apf__select ${errors.involvement ? "apf__input--err" : ""}`}
                  value={form.involvement}
                  onChange={e => set("involvement", e.target.value)}
                >
                  <option value="">Select involvement level</option>
                  {FIELDS.involvement.map(o => <option key={o}>{o}</option>)}
                </select>
              </Field>
              <Field label="Commercial Engagement Model *" error={errors.commercialModel}>
                <select
                  className={`apf__input apf__select ${errors.commercialModel ? "apf__input--err" : ""}`}
                  value={form.commercialModel}
                  onChange={e => set("commercialModel", e.target.value)}
                >
                  <option value="">Select model</option>
                  {FIELDS.commercialModel.map(o => <option key={o}>{o}</option>)}
                </select>
              </Field>
            </div>
          </div>
        )}

        <div className="apf__actions">
          {step > 0 && (
            <p  className="apf__btn apf__btn--ghost" onClick={back}>
              ← Back
            </p>
          )}
          {step < 2 ? (
            <p className="apf__btn apf__btn--primary" onClick={next}>
              Continue →
            </p>
          ) : (
            <button type="submit" className="apf__btn apf__btn--primary" disabled={isSubmitting}>
              {isSubmitting ? "Submitting..." : "Submit Application ✦"}
            </button>
          )}
        </div>
      </form>
    </div>
  );
}

function Field({ label, error, children }) {
  return (
    <div className="apf__field">
      <label className="apf__label">{label}</label>
      {children}
      {error && <span className="apf__error">{error}</span>}
    </div>
  );
}